<article
  class="alert-card"
  [class.alert-card--applied]="status() === 'applied'"
  [class.alert-card--muted]="status() === 'dismissed' || status() === 'forbidden'"
  [attr.aria-label]="'$ai.alerts.card-aria' | translateAsync: { name: proposal().name }"
  [attr.aria-busy]="applying()"
>
  <header class="alert-card__head">
    <mat-icon class="alert-card__icon" aria-hidden="true">notifications_active</mat-icon>
    <div class="alert-card__title">
      <span class="alert-card__op">{{ '$ai.alerts.op.' + proposal().operation | translateAsync }}</span>
      <h3>{{ proposal().name }}</h3>
    </div>
    @if (severity(); as severity) {
      <span
        class="alert-card__severity"
        [style.--severity-color]="severityColor()"
      >
        <span class="alert-card__dot" aria-hidden="true"></span>
        <span aria-hidden="true">{{ '$condition.severity-' + severity | translateAsync }}</span>
        <span class="alert-card__sr">{{ '$ai.alerts.severity-aria' | translateAsync: { label: ('$condition.severity-' + severity | translateAsync) } }}</span>
      </span>
    }
  </header>

  @switch (status()) {
    @case ('applying') {
      <p class="alert-card__status" role="status" aria-live="polite" tabindex="-1" #focusTarget>
        {{ '$ai.alerts.busy.' + proposal().operation | translateAsync }}
      </p>
    }
    @case ('applied') {
      <p class="alert-card__status alert-card__status--done" role="status" aria-live="polite" tabindex="-1" #focusTarget>
        <mat-icon aria-hidden="true">check_circle</mat-icon>
        {{ '$ai.alerts.done.' + proposal().operation | translateAsync }}
      </p>
    }
    @case ('dismissed') {
      <p class="alert-card__status" role="status" aria-live="polite" tabindex="-1" #focusTarget>
        <mat-icon aria-hidden="true">block</mat-icon>
        {{ '$ai.alerts.done.dismissed' | translateAsync }}
      </p>
    }
    @case ('forbidden') {
      <p class="alert-card__status alert-card__status--note" role="status" aria-live="polite" tabindex="-1" #focusTarget>
        <mat-icon aria-hidden="true">lock</mat-icon>
        {{ '$ai.alerts.error.forbidden' | translateAsync }}
      </p>
    }
    @case ('error') {
      <p class="alert-card__error" role="alert" tabindex="-1" #focusTarget>
        {{ '$ai.alerts.error.' + (state().error?.key ?? 'generic') | translateAsync }}
      </p>
    }
  }

  @if (proposal().changes?.length) {
    <section class="alert-card__changes">
      <h4>{{ '$ai.alerts.changes.title' | translateAsync }}</h4>
      <ul>
        @for (change of proposal().changes; track $index) {
          <li>{{ change }}</li>
        }
      </ul>
    </section>
  }

  <dl class="alert-card__summary">
    <div>
      <dt>{{ '$ai.alerts.summary.watch' | translateAsync }}</dt>
      <dd>{{ proposal().summary.watch }}</dd>
    </div>
    <div>
      <dt>{{ '$ai.alerts.summary.frequency' | translateAsync }}</dt>
      <dd>{{ proposal().summary.frequency }}</dd>
    </div>
    <div>
      <dt>{{ '$ai.alerts.summary.notify' | translateAsync }}</dt>
      <dd>{{ proposal().summary.notify }}</dd>
    </div>
    @for (message of messages(); track $index) {
      @if (message.kind === 'email') {
        <div class="alert-card__mail">
          <dt>{{ '$ai.alerts.summary.mail' | translateAsync }}</dt>
          <dd>
            @if (message.subject) {
              <span class="alert-card__mail-subject-label alert-card__sr">{{ '$ai.alerts.summary.subject' | translateAsync }}</span>&ngsp;
              <strong class="alert-card__mail-subject">{{ message.subject }}</strong>
            }
            @if (message.body) {
              <span class="alert-card__mail-body">{{ message.body }}</span>
            }
          </dd>
        </div>
      } @else {
        <div class="alert-card__notification">
          <dt>{{ '$ai.alerts.summary.notification' | translateAsync }}</dt>
          <dd>
            @if (message.title) {
              <span class="alert-card__notification-title-label alert-card__sr">{{ '$ai.alerts.summary.title' | translateAsync }}</span>&ngsp;
              <strong class="alert-card__notification-title">{{ message.title }}</strong>
            }
            @if (message.body) {
              <span class="alert-card__notification-body">{{ message.body }}</span>
            }
          </dd>
        </div>
      }
    }
  </dl>

  @if (count(); as count) {
    <p class="alert-card__count">
      <mat-icon aria-hidden="true">info</mat-icon>
      <span>
        @let sentence = splitAtNumber('$ai.alerts.count.' + count.key | translateAsync: { count: numberMark });
        {{ sentence.before }}@if (sentence.after !== null) {<strong>{{ formatCount(count.rows) }}</strong>{{ sentence.after }}}
      </span>
    </p>
  }

  @if (status() === 'applied') {
    <div class="alert-card__links">
      <a mat-stroked-button [routerLink]="conditionLink()" class="alert-card__link-condition">
        <mat-icon aria-hidden="true">rule</mat-icon>
        {{ '$ai.alerts.link.condition' | translateAsync }}
      </a>
      <a mat-stroked-button routerLink="/events-config" [queryParams]="eventQueryParams()" class="alert-card__link-event">
        <mat-icon aria-hidden="true">event</mat-icon>
        {{ '$ai.alerts.link.event' | translateAsync }}
      </a>
    </div>
  } @else if (!closed()) {
    <div class="alert-card__actions">
      @if (showConfirm()) {
        <button
          mat-flat-button
          type="button"
          class="alert-card__confirm"
          disabledInteractive
          [disabled]="applying()"
          (click)="confirm()"
        >
          @if (applying()) {
            <mat-spinner matButtonIcon diameter="16" aria-hidden="true"></mat-spinner>
          } @else if (status() === 'error') {
            <mat-icon aria-hidden="true">refresh</mat-icon>
          }
          {{ confirmLabel() | translateAsync }}
        </button>
      }
      <button mat-button type="button" class="alert-card__dismiss" [class.alert-card__dismiss--primary]="retryBlocked()" disabledInteractive [disabled]="applying()" (click)="dismiss()">
        {{ '$ai.alerts.dismiss' | translateAsync }}
      </button>
    </div>
  }
</article>
